// client/src/hooks/usePmPortalApiClient.js
// Targets /api/pm-portal and reads the pmPortalToken key. No code path here ever
// touches localStorage['jwtToken'] or ['clientPortalToken'].
//
// The 403 handling is deliberate and was learned the hard way in the staff client:
// 403 means authenticated-but-not-permitted, which is a normal, expected answer once
// row-level authority exists. Treating it as a logout ejected people from the app and
// hid every deliberate server refusal behind "session expired". Only 401 logs out.
import { useCallback, useMemo } from 'react';

const TOKEN_KEY = 'pmPortalToken';
const ROLE_KEY = 'pmPortalRole';

const forceLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    window.location.href = '/pm-portal/login';
};

export const usePmPortalApiClient = () => {
    const token = localStorage.getItem(TOKEN_KEY);

    const request = useCallback(async (endpoint, options = {}, responseType = null) => {
        const config = {
            ...options,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers,
            },
        };

        const response = await fetch(endpoint, config);

        if (response.status === 401) {
            forceLogout();
            throw new Error('Your session has expired. Please sign in again.');
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.mustChangePassword) {
                window.location.href = '/pm-portal/change-password';
            }
            const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
            err.status = response.status;
            err.payload = errorData;
            throw err;
        }

        if (responseType === 'blob') {
            return { data: await response.blob() };
        }
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
            return response.json();
        }
        return null;
    }, [token]);

    return useMemo(() => ({
        get: (endpoint, opts = {}) => request(endpoint, { method: 'GET' }, opts.responseType || null),
        post: (endpoint, body) => request(endpoint, { method: 'POST', body: JSON.stringify(body) }),
    }), [request]);
};
